import React, { useEffect, useState } from 'react';
import { ArrowRight, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { listCompanyNews, resolveFileUrl } from '../services/api';

const formatDate = (raw) => {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const CompanyNewsSection = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listCompanyNews(6)
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section
      className="bg-[#e2ede6] pb-12 lg:pb-16"
      data-testid="home-company-news-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">
              From Emerald Corporates
            </span>
            <h2
              className="mt-3 text-4xl lg:text-5xl text-[#0b1a2e] leading-[1.05]"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}
            >
              Newsroom
            </h2>
            <p className="mt-3 max-w-xl text-[#0b1a2e]/70">
              Press releases, corporate announcements and coverage of our companies across
              global media.
            </p>
          </div>
          <Link
            to="/newsroom"
            data-testid="newsroom-view-all-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#0f5c3d]/25 text-[#0b1a2e] text-sm hover:bg-white transition"
          >
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {loading ? (
          <div className="bg-white/60 rounded-2xl p-10 text-center text-[#0b1a2e]/60 border border-black/5">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white/60 rounded-2xl p-10 text-center text-[#0b1a2e]/60 border border-black/5">
            No corporate news published yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.slice(0, 3).map((n) => (
              <Link
                key={n.id}
                to={`/newsroom/${n.slug}`}
                data-testid="company-news-card"
                className="group bg-white rounded-2xl overflow-hidden border border-black/5 shadow-sm hover:shadow-xl transition-all duration-300"
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-[#e2ede6]">
                  {n.image_url ? (
                    <img
                      src={resolveFileUrl(n.image_url)}
                      alt={n.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-tr from-[#0b1a2e] to-[#0f5c3d]" />
                  )}
                </div>
                <div className="p-5">
                  <div className="text-xs font-semibold tracking-widest text-[#0f5c3d] uppercase">
                    {formatDate(n.published_at)}
                  </div>
                  <h3 className="mt-2 text-[17px] font-semibold leading-snug text-[#0b1a2e] line-clamp-2 group-hover:text-[#0f5c3d] transition-colors">
                    {n.title}
                  </h3>
                  <p className="mt-2 text-sm text-[#0b1a2e]/60 line-clamp-3">
                    {n.short_desc}
                  </p>
                  <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#0f5c3d]">
                    Read more <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default CompanyNewsSection;
